import { useMemo } from 'react';
import { HubsDataDictionary, SchedulerDayColumn } from '@lp/features/Scheduler/types';
import { getShiftSiblingsMap } from '@lp/features/Scheduler/utils';
import { getCSSiblingDataGetter } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/utils';
import { CSResource, CSResourcesWithShifts, CSShiftType } from '@/api/apis/resources/schedule';
import { OrganType } from '@/api/apis/case';

export const useCSShiftSiblingMaps = (
  dayCols: SchedulerDayColumn[],
  userList: CSResource[],
  shiftsData: CSResourcesWithShifts,
  targetHubId: string,
  hubsDictionary: HubsDataDictionary,
) => {
  const siblingDataGetter = useMemo(
    () => getCSSiblingDataGetter(targetHubId, hubsDictionary),
    [targetHubId, hubsDictionary],
  );

  const siblingMaps: Record<string, ReturnType<typeof getShiftSiblingsMap>> = useMemo(() => {
    return userList.reduce(
      (acc, u) => ({ ...acc, [u.id]: getShiftSiblingsMap(dayCols, shiftsData[u.id].days, siblingDataGetter) }),
      {},
    );
  }, [dayCols, userList, shiftsData, siblingDataGetter]);

  return siblingMaps;
};

type DayTotals = Record<OrganType | 'total', number>;

export const useCSOnCallDayTotalsMap = (
  dayCols: SchedulerDayColumn[],
  userList: CSResource[],
  shiftsData: CSResourcesWithShifts,
  hubIds: string[],
) => {
  return useMemo(() => {
    const totals = new Map<string, DayTotals>();

    dayCols.forEach((column) => {
      const dayTotals: DayTotals = {
        total: 0,
        [OrganType.Lungs]: 0,
        [OrganType.Heart]: 0,
        [OrganType.Liver]: 0,
        [OrganType.Kidney]: 0,
      };
      userList.forEach((user) => {
        const shift = shiftsData[user.id].days[column.key];
        const isRelatedShift = shift?.hubId ? hubIds.includes(shift?.hubId) : false;

        if (shift && isRelatedShift && shift.shiftType === CSShiftType.OnCall && shift.organs) {
          // eslint-disable-next-line no-return-assign
          shift.organs.forEach((organ) => (dayTotals[organ] += 1));
          dayTotals.total += 1;
        }
      });

      totals.set(column.key, dayTotals);
    });

    return totals;
  }, [dayCols, hubIds, shiftsData, userList]);
};
